import { useState } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import logoFull from '../../assets/images/logo_aktivity.png';
import waitlistService from '../../services/waitlistService';

/**
 * Waitlist Landing Page
 * One-page waitlist website for Aktivity.io
 * Enhanced with professional animations and visual effects
 */

const WaitlistPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    role: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.role) {
      toast.error('Please fill in all fields');
      return;
    }

    // Basic email validation
    const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setIsSubmitting(true);

    try {
      await waitlistService.join(formData);
      setIsSubmitted(true);
      toast.success('You\'re on the waitlist! We\'ll be in touch soon.');
      setFormData({ name: '', email: '', role: '' });
    } catch (error) {
      console.error('Waitlist submission error:', error);
      toast.error(error.message || 'Failed to join waitlist. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const scrollToForm = () => {
    document.getElementById('waitlist-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 40 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: 'easeOut' } },
  };

  const fadeInScale = {
    hidden: { opacity: 0, scale: 0.9 },
    visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: 'easeOut' } },
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { staggerChildren: 0.15, delayChildren: 0.1 } },
  };

  const cardHover = {
    rest: { scale: 1, y: 0 },
    hover: { scale: 1.02, y: -8, transition: { duration: 0.3, ease: 'easeOut' } },
  };

  // Floating animation for decorative elements
  const floatingAnimation = {
    y: [0, -15, 0],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  };

  const floatingAnimationSlow = {
    y: [0, -10, 0],
    rotate: [0, 5, 0],
    transition: {
      duration: 6,
      repeat: Infinity,
      ease: 'easeInOut',
    },
  };

  return (
    <div className="min-h-screen bg-white overflow-x-hidden">
      {/* Navigation */}
      <motion.nav
        initial={{ y: -100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: 'easeOut' }}
        className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-xl z-50 border-b border-neutral-100/50 shadow-sm"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <motion.img
              src={logoFull}
              alt="Aktivity"
              className="h-8"
              whileHover={{ scale: 1.05 }}
              transition={{ duration: 0.2 }}
            />
            <motion.button
              onClick={scrollToForm}
              className="px-5 py-2.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-medium rounded-xl transition-all text-sm shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/30"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              Join Waitlist
            </motion.button>
          </div>
        </div>
      </motion.nav>

      {/* 1. Hero Section */}
      <section className="pt-24 pb-16 sm:pt-32 sm:pb-24 relative overflow-hidden">
        {/* Animated background gradient */}
        <div className="absolute inset-0 bg-gradient-to-br from-orange-50/80 via-white to-blue-50/50"></div>

        {/* Floating decorative elements */}
        <motion.div
          animate={floatingAnimation}
          className="absolute top-32 right-10 w-72 h-72 bg-gradient-to-br from-orange-400/10 to-orange-600/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={floatingAnimationSlow}
          className="absolute bottom-20 left-10 w-96 h-96 bg-gradient-to-br from-blue-400/10 to-purple-600/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ ...floatingAnimation, transition: { ...floatingAnimation.transition, delay: 1 } }}
          className="absolute top-1/2 left-1/3 w-32 h-32 bg-gradient-to-br from-green-400/10 to-teal-600/10 rounded-full blur-2xl"
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left - Text */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={fadeInUp}
            >
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2, duration: 0.5 }}
                className="inline-flex items-center gap-2 px-4 py-2 bg-orange-100/80 backdrop-blur-sm rounded-full text-orange-700 text-sm font-medium mb-6 shadow-sm"
              >
                <span className="w-2 h-2 bg-orange-500 rounded-full animate-pulse"></span>
                Now accepting early access signups
              </motion.div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-neutral-900 leading-tight mb-6">
                Stop Measuring Trainings with Just{' '}
                <span className="relative">
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-500 to-orange-600">Happy Sheets.</span>
                  <motion.span
                    className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-orange-500 to-orange-600 rounded-full"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 0.8, duration: 0.6 }}
                  />
                </span>
              </h1>
              <p className="text-lg sm:text-xl text-neutral-600 mb-8 leading-relaxed">
                Aktivity turns your sessions into <em className="text-neutral-800 font-medium">actionable insights</em> – capturing challenges,
                action plans & real engagement, not just smiley-face feedback.
              </p>
              <p className="text-sm text-neutral-500 mb-6 flex items-center gap-2">
                <svg className="w-4 h-4 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                Get early access, special pricing, and help shape the future of learning analytics.
              </p>

              {/* Inline Form */}
              <motion.form
                id="waitlist-form"
                onSubmit={handleSubmit}
                className="space-y-4"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.5 }}
              >
                <div className="flex flex-col sm:flex-row gap-3">
                  <motion.input
                    type="text"
                    name="name"
                    placeholder="Your Name"
                    value={formData.name}
                    onChange={handleInputChange}
                    className="flex-1 px-5 py-3.5 bg-white border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 shadow-sm hover:shadow-md transition-all duration-300 placeholder:text-neutral-400"
                    whileFocus={{ scale: 1.01 }}
                  />
                  <motion.input
                    type="email"
                    name="email"
                    placeholder="Your Email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="flex-1 px-5 py-3.5 bg-white border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 shadow-sm hover:shadow-md transition-all duration-300 placeholder:text-neutral-400"
                    whileFocus={{ scale: 1.01 }}
                  />
                </div>
                <div className="flex flex-col sm:flex-row gap-3">
                  <motion.select
                    name="role"
                    value={formData.role}
                    onChange={handleInputChange}
                    className="flex-1 px-5 py-3.5 bg-white border border-neutral-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-500/50 focus:border-orange-500 text-neutral-700 shadow-sm hover:shadow-md transition-all duration-300 cursor-pointer"
                    whileFocus={{ scale: 1.01 }}
                  >
                    <option value="">I am a...</option>
                    <option value="trainer">Corporate Trainer</option>
                    <option value="ld">L&D Professional</option>
                    <option value="coach">Coach</option>
                    <option value="teacher">Teacher / Educator</option>
                    <option value="other">Other</option>
                  </motion.select>
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    className="px-8 py-3.5 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-xl transition-all disabled:opacity-70 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/35"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {isSubmitting ? (
                      <>
                        <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                        </svg>
                        Joining...
                      </>
                    ) : (
                      'Join the Waitlist'
                    )}
                  </motion.button>
                </div>
              </motion.form>

              {isSubmitted && (
                <motion.div
                  initial={{ opacity: 0, y: 10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3"
                >
                  <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0">
                    <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  </div>
                  <div>
                    <p className="font-semibold text-green-800">You're on the list!</p>
                    <p className="text-sm text-green-600">Check your email for confirmation.</p>
                  </div>
                </motion.div>
              )}
            </motion.div>

            {/* Right - Illustration */}
            <motion.div
              initial={{ opacity: 0, scale: 0.9, x: 50 }}
              animate={{ opacity: 1, scale: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: 'easeOut' }}
              className="hidden lg:block"
            >
              <div className="relative">
                {/* Glow effect behind card */}
                <div className="absolute inset-0 bg-gradient-to-r from-orange-400 to-orange-600 rounded-3xl transform rotate-3 opacity-20 blur-xl"></div>
                <motion.div
                  className="relative bg-white/90 backdrop-blur-sm rounded-3xl shadow-2xl p-8 border border-neutral-100"
                  whileHover={{ y: -5 }}
                  transition={{ duration: 0.3 }}
                >
                  <div className="space-y-4">
                    <div className="flex items-center gap-3 mb-6">
                      <motion.div
                        className="w-12 h-12 bg-gradient-to-br from-orange-400 to-orange-600 rounded-xl flex items-center justify-center shadow-lg shadow-orange-500/30"
                        animate={{ rotate: [0, 5, 0] }}
                        transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
                      >
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                        </svg>
                      </motion.div>
                      <span className="font-bold text-neutral-900 text-lg">Session Analytics</span>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      {[
                        { value: '91%', label: 'Engagement', color: 'green', delay: 0 },
                        { value: '126', label: 'Poll Responses', color: 'blue', delay: 0.1 },
                        { value: '112', label: 'Challenges', color: 'purple', delay: 0.2 },
                        { value: '78', label: 'Action Plans', color: 'orange', delay: 0.3 },
                      ].map((stat, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ delay: 0.6 + stat.delay, duration: 0.4 }}
                          whileHover={{ scale: 1.05, y: -3 }}
                          className={`bg-${stat.color}-50 rounded-xl p-4 cursor-default transition-shadow hover:shadow-md`}
                        >
                          <motion.p
                            className={`text-2xl font-bold text-${stat.color}-600`}
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ delay: 0.8 + stat.delay }}
                          >
                            {stat.value}
                          </motion.p>
                          <p className="text-sm text-neutral-600">{stat.label}</p>
                        </motion.div>
                      ))}
                    </div>
                    <motion.div
                      className="flex items-center gap-2 text-sm text-neutral-500 mt-4 pt-4 border-t border-neutral-100"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      transition={{ delay: 1.2 }}
                    >
                      <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                      <span>Live Session Data</span>
                      <span className="ml-auto text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">Active</span>
                    </motion.div>
                  </div>
                </motion.div>

                {/* Floating badge */}
                <motion.div
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 1, type: 'spring', stiffness: 200 }}
                  className="absolute -top-4 -right-4 bg-white rounded-xl shadow-xl p-3 border border-neutral-100"
                >
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 bg-green-500 rounded-full flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <span className="text-sm font-semibold text-neutral-800">Real-time</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </div>

          {/* Trust indicators */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8, duration: 0.6 }}
            className="mt-16 pt-8 border-t border-neutral-200/50"
          >
            <p className="text-center text-sm text-neutral-500 mb-6">Trusted by forward-thinking L&D teams</p>
            <div className="flex flex-wrap justify-center items-center gap-8 opacity-50">
              {['Enterprise', 'Startup', 'Agency', 'Education'].map((item, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 1 + i * 0.1 }}
                  className="text-neutral-400 font-bold text-lg"
                >
                  {item}
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* 2. Problem Section */}
      <section className="py-16 sm:py-24 bg-neutral-900 text-white relative overflow-hidden">
        {/* Animated background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }}></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp}
            className="text-center mb-12"
          >
            <motion.span
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              className="inline-block px-4 py-2 bg-orange-500/20 rounded-full text-orange-400 text-sm font-medium mb-4"
            >
              Sound Familiar?
            </motion.span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              If This Is Your Reality,{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-orange-400 to-orange-600">
                Aktivity Is Built for You.
              </span>
            </h2>
            <p className="text-neutral-400 text-lg max-w-2xl mx-auto">
              You spend hours designing sessions. But when it comes to measuring impact...
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
          >
            {[
              { icon: '📋', text: 'Feedback is still a Google Form or a smiley-sheet passed around at the end.' },
              { icon: '📉', text: 'Only 20–30% of participants fill it – and mostly the happy or the unhappy ones.' },
              { icon: '🤷', text: 'You have no record of challenges discussed in the room.' },
              { icon: '📝', text: 'Action plans are written on paper and never seen again.' },
              { icon: '😅', text: 'L&D asks you for "impact stories", and all you have is "They seemed engaged."' },
              { icon: '🔄', text: 'You repeat the same content without knowing what actually worked.' },
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                initial="rest"
                whileHover="hover"
                animate="rest"
              >
                <motion.div
                  variants={cardHover}
                  className="bg-gradient-to-br from-neutral-800 to-neutral-800/50 rounded-2xl p-6 border border-neutral-700/50 hover:border-orange-500/50 transition-colors h-full backdrop-blur-sm shadow-xl"
                >
                  <motion.span
                    className="text-4xl mb-4 block"
                    whileHover={{ scale: 1.2, rotate: 10 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    {item.icon}
                  </motion.span>
                  <p className="text-neutral-300 leading-relaxed">{item.text}</p>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mt-12"
          >
            <p className="text-xl text-neutral-400 mb-6">Tired of this?</p>
            <motion.button
              onClick={scrollToForm}
              className="px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/35"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              Join the Waitlist & Get Early Access
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* 3. Solution Snapshot */}
      <section className="py-16 sm:py-24 bg-white relative overflow-hidden">
        {/* Subtle background decoration */}
        <div className="absolute top-0 right-0 w-1/2 h-1/2 bg-gradient-to-bl from-orange-50 to-transparent opacity-50"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-block px-4 py-2 bg-blue-100 rounded-full text-blue-700 text-sm font-medium mb-4"
            >
              The Solution
            </motion.span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 mb-4">
              What Is Aktivity?
            </h2>
            <p className="text-lg text-neutral-600 max-w-3xl mx-auto">
              Aktivity is a digital companion for Trainers & L&D that{' '}
              <strong className="text-neutral-800">captures engagement, challenges, and action plans topic-wise</strong> – and turns them into{' '}
              <strong className="text-neutral-800">beautiful reports</strong> for participants and L&D.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8"
          >
            {[
              {
                phase: 'Before',
                title: 'Before the Session',
                description: 'Simple digital workbooks & pre-questions to understand expectations.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                  </svg>
                ),
                color: 'blue',
                gradient: 'from-blue-500 to-blue-600',
              },
              {
                phase: 'During',
                title: 'During the Session',
                description: 'Polls, word clouds, quizzes, and open-text questions – all in one place. Topic-wise challenges & reflections captured as they happen.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M13 10V3L4 14h7v7l9-11h-7z" />
                  </svg>
                ),
                color: 'orange',
                gradient: 'from-orange-500 to-orange-600',
              },
              {
                phase: 'After',
                title: 'After the Session',
                description: 'Individual learner reports with their notes & action plans. L&D summary reports with insights, themes & engagement trends.',
                icon: (
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                ),
                color: 'green',
                gradient: 'from-green-500 to-green-600',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInScale}
                initial="rest"
                whileHover="hover"
                animate="rest"
              >
                <motion.div
                  variants={cardHover}
                  className={`relative p-8 rounded-3xl border-2 ${
                    item.color === 'blue' ? 'border-blue-200 bg-gradient-to-br from-blue-50 to-white' :
                    item.color === 'orange' ? 'border-orange-200 bg-gradient-to-br from-orange-50 to-white' :
                    'border-green-200 bg-gradient-to-br from-green-50 to-white'
                  } shadow-lg hover:shadow-2xl transition-all duration-300 h-full`}
                >
                  {/* Step number */}
                  <div className="absolute -top-3 -right-3 w-8 h-8 bg-white rounded-full shadow-md flex items-center justify-center text-sm font-bold text-neutral-600">
                    {index + 1}
                  </div>

                  <motion.div
                    className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 bg-gradient-to-br ${item.gradient} text-white shadow-lg`}
                    whileHover={{ rotate: 5, scale: 1.1 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    {item.icon}
                  </motion.div>
                  <span className={`text-sm font-semibold uppercase tracking-wide ${
                    item.color === 'blue' ? 'text-blue-600' :
                    item.color === 'orange' ? 'text-orange-600' :
                    'text-green-600'
                  }`}>
                    {item.phase}
                  </span>
                  <h3 className="text-xl font-bold text-neutral-900 mt-2 mb-3">{item.title}</h3>
                  <p className="text-neutral-600 leading-relaxed">{item.description}</p>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mt-12"
          >
            <p className="text-lg text-neutral-600 mb-6">Want to be among the first to use this?</p>
            <motion.button
              onClick={scrollToForm}
              className="px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/35"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              Join Aktivity Waitlist
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* 4. Who It's For */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-neutral-50 to-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-block px-4 py-2 bg-purple-100 rounded-full text-purple-700 text-sm font-medium mb-4"
            >
              Who It's For
            </motion.span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 mb-4">
              Built for People Who Take{' '}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
                Learning Seriously.
              </span>
            </h2>
            <p className="text-lg text-neutral-600">Aktivity is for you if you are:</p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            variants={staggerContainer}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6"
          >
            {[
              {
                title: 'Corporate Trainers & Facilitators',
                description: 'Run ILT / VILT programs and want to show impact beyond "participants were engaged."',
                icon: '🎯',
                color: 'orange',
              },
              {
                title: 'L&D & HR Teams',
                description: 'Need evidence to justify training budgets and design better journeys.',
                icon: '📊',
                color: 'blue',
              },
              {
                title: 'Coaches & Course Creators',
                description: 'Run free or paid webinars and want insights on challenges, objections & readiness before offering your programs.',
                icon: '💡',
                color: 'purple',
              },
              {
                title: 'Teachers & Educators',
                description: 'Want class-wise reports for parents and better visibility into student effort, not just marks.',
                icon: '📚',
                color: 'green',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInScale}
                initial="rest"
                whileHover="hover"
                animate="rest"
              >
                <motion.div
                  variants={cardHover}
                  className="bg-white rounded-2xl p-6 shadow-lg border border-neutral-100 hover:border-orange-200 transition-all h-full group"
                >
                  <motion.span
                    className="text-5xl mb-4 block"
                    whileHover={{ scale: 1.2, rotate: 10 }}
                    transition={{ type: 'spring', stiffness: 300 }}
                  >
                    {item.icon}
                  </motion.span>
                  <h3 className="text-lg font-bold text-neutral-900 mb-2 group-hover:text-orange-600 transition-colors">{item.title}</h3>
                  <p className="text-neutral-600 text-sm leading-relaxed">{item.description}</p>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mt-12"
          >
            <p className="text-neutral-600 mb-6">If you see yourself in one of these, get early access.</p>
            <motion.button
              onClick={scrollToForm}
              className="px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/35"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              Join the Waitlist
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* 5. Key Features */}
      <section className="py-16 sm:py-24 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-block px-4 py-2 bg-green-100 rounded-full text-green-700 text-sm font-medium mb-4"
            >
              Features
            </motion.span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 mb-4">
              What You'll Be Able to Do with Aktivity
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            variants={staggerContainer}
            className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
          >
            {[
              {
                icon: '📒',
                title: 'Digital Session Workbooks',
                description: 'Replace paper handouts with smart, structured workbooks participants can fill live.',
              },
              {
                icon: '🧠',
                title: 'Topic-wise Challenges & Reflections',
                description: 'Capture what participants are struggling with – by topic, not just at the end.',
              },
              {
                icon: '✅',
                title: 'Action Plans That Don\'t Disappear',
                description: 'Participants leave with clear next steps, and you have a record of them.',
              },
              {
                icon: '📈',
                title: 'Engagement & Impact Reports',
                description: 'Show L&D who engaged, with what, and how – beyond attendance and NPS.',
              },
              {
                icon: '🔌',
                title: 'Plays Well with Your Tech Stack',
                description: 'Designed to fit into your existing tools (Zoom/Teams, LMS, TMS) – not replace them.',
              },
              {
                icon: '🎨',
                title: 'Beautiful, Shareable Reports',
                description: 'Individual and group reports that look professional and tell the full story.',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                className="flex gap-4 group"
              >
                <motion.span
                  className="text-4xl flex-shrink-0"
                  whileHover={{ scale: 1.2, rotate: 10 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  {item.icon}
                </motion.span>
                <div>
                  <h3 className="text-lg font-bold text-neutral-900 mb-2 group-hover:text-orange-600 transition-colors">{item.title}</h3>
                  <p className="text-neutral-600 leading-relaxed">{item.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>

          {/* Founding Early Adopters Banner */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="mt-16 relative overflow-hidden"
          >
            <div className="absolute inset-0 bg-gradient-to-r from-orange-500 to-orange-600 rounded-3xl"></div>
            <div className="absolute inset-0 opacity-10">
              <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '30px 30px' }}></div>
            </div>
            <div className="relative p-8 sm:p-12 text-center text-white">
              <motion.span
                className="text-5xl mb-4 block"
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 2, repeat: Infinity, ease: 'easeInOut' }}
              >
                🚀
              </motion.span>
              <h3 className="text-2xl sm:text-3xl font-bold mb-4">Founding Early Adopters</h3>
              <p className="text-orange-100 mb-6 max-w-2xl mx-auto text-lg">
                Limited early-access spots for Trainers, Coaches & L&D teams who want to co-create with us.
              </p>
              <motion.button
                onClick={scrollToForm}
                className="px-8 py-4 bg-white text-orange-600 font-semibold rounded-xl hover:bg-orange-50 transition-all shadow-xl"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.98 }}
              >
                Join the Waitlist – Become a Founding Early Adopter
              </motion.button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 6. Why Join Waitlist */}
      <section className="py-16 sm:py-24 bg-neutral-900 relative overflow-hidden">
        {/* Animated gradient orbs */}
        <motion.div
          animate={{ x: [0, 100, 0], y: [0, -50, 0] }}
          transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute top-20 left-10 w-96 h-96 bg-orange-500/10 rounded-full blur-3xl"
        />
        <motion.div
          animate={{ x: [0, -100, 0], y: [0, 50, 0] }}
          transition={{ duration: 25, repeat: Infinity, ease: 'easeInOut' }}
          className="absolute bottom-20 right-10 w-96 h-96 bg-blue-500/10 rounded-full blur-3xl"
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-block px-4 py-2 bg-orange-500/20 rounded-full text-orange-400 text-sm font-medium mb-4"
            >
              Why Join Now
            </motion.span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4">
              Why Join the Aktivity Waitlist Now?
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            variants={staggerContainer}
            className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8"
          >
            {[
              {
                icon: '🎁',
                title: 'Early-bird Pricing',
                description: 'Lifetime discounts for our first wave of users.',
              },
              {
                icon: '🚀',
                title: 'Priority Access',
                description: 'Beta features before public launch.',
              },
              {
                icon: '🧠',
                title: 'Shape the Product',
                description: 'Your feedback influences what gets built.',
              },
              {
                icon: '🌟',
                title: 'Founding Partner',
                description: 'Visibility as a Founding Trainer / Early Partner.',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInScale}
                className="text-center text-white group"
              >
                <motion.div
                  className="w-20 h-20 bg-gradient-to-br from-neutral-800 to-neutral-700 rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl group-hover:shadow-2xl group-hover:shadow-orange-500/20 transition-all"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  <span className="text-4xl">{item.icon}</span>
                </motion.div>
                <h3 className="text-lg font-bold text-white mb-2">{item.title}</h3>
                <p className="text-neutral-400">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mt-12"
          >
            <p className="text-neutral-400 mb-6">
              If you believe training deserves better than happy sheets, you'll love what we're building.
            </p>
            <motion.button
              onClick={scrollToForm}
              className="px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/35"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              Join the Waitlist
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* 7. How It Works */}
      <section className="py-16 sm:py-24 bg-white relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-block px-4 py-2 bg-cyan-100 rounded-full text-cyan-700 text-sm font-medium mb-4"
            >
              Simple Process
            </motion.span>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-neutral-900 mb-4">
              How the Waitlist Works
            </h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            variants={staggerContainer}
            className="grid md:grid-cols-3 gap-8 relative"
          >
            {/* Connecting line */}
            <div className="hidden md:block absolute top-16 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-orange-200 via-orange-400 to-orange-200"></div>

            {[
              {
                step: '1',
                title: 'Sign up for the waitlist',
                description: 'Share your email and role (Trainer, L&D, Coach, Teacher).',
              },
              {
                step: '2',
                title: 'We reach out in phases',
                description: 'Starting with certain regions and use-cases.',
              },
              {
                step: '3',
                title: 'You get early access & perks',
                description: 'And we build Aktivity with you, not for you.',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInScale}
                className="text-center relative"
              >
                <motion.div
                  className="w-20 h-20 bg-gradient-to-br from-orange-500 to-orange-600 text-white text-3xl font-bold rounded-2xl flex items-center justify-center mx-auto mb-6 shadow-xl shadow-orange-500/30 relative z-10"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                  transition={{ type: 'spring', stiffness: 300 }}
                >
                  {item.step}
                </motion.div>
                <h3 className="text-xl font-bold text-neutral-900 mb-2">{item.title}</h3>
                <p className="text-neutral-600 leading-relaxed">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* 8. FAQ Section */}
      <section className="py-16 sm:py-24 bg-gradient-to-b from-neutral-50 to-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            variants={fadeInUp}
            className="text-center mb-16"
          >
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-block px-4 py-2 bg-amber-100 rounded-full text-amber-700 text-sm font-medium mb-4"
            >
              Questions?
            </motion.span>
            <h2 className="text-3xl sm:text-4xl font-bold text-neutral-900 mb-4">FAQs</h2>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-50px' }}
            variants={staggerContainer}
            className="space-y-4"
          >
            {[
              {
                q: 'Is Aktivity live yet?',
                a: 'We\'re currently in the build & early testing phase. The waitlist helps us onboard serious early adopters first and design features around their real sessions.',
              },
              {
                q: 'Do I have to pay to join the waitlist?',
                a: 'No. Joining the waitlist is free. You\'ll get early access, behind-the-scenes updates, and special launch offers when we go live.',
              },
              {
                q: 'Who will get access first?',
                a: 'Trainers, L&D teams, and coaches who actively run sessions and are willing to test and share feedback will be prioritized.',
              },
              {
                q: 'Will it work with virtual as well as in-person trainings?',
                a: 'Yes. Aktivity is designed for ILT, VILT, hybrid programs and webinars.',
              },
            ].map((item, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                initial="rest"
                whileHover="hover"
                animate="rest"
              >
                <motion.div
                  variants={{ rest: { scale: 1 }, hover: { scale: 1.01 } }}
                  className="bg-white rounded-2xl p-6 shadow-lg hover:shadow-xl border border-neutral-100 transition-all"
                >
                  <h3 className="text-lg font-bold text-neutral-900 mb-2 flex items-center gap-3">
                    <span className="w-8 h-8 bg-orange-100 text-orange-600 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0">
                      Q
                    </span>
                    {item.q}
                  </h3>
                  <p className="text-neutral-600 pl-11 leading-relaxed">{item.a}</p>
                </motion.div>
              </motion.div>
            ))}
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="text-center mt-12"
          >
            <p className="text-neutral-600 mb-6">Ready to help us fix training measurement?</p>
            <motion.button
              onClick={scrollToForm}
              className="px-8 py-4 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold rounded-xl transition-all shadow-lg shadow-orange-500/25 hover:shadow-xl hover:shadow-orange-500/35"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              Join the Aktivity Waitlist
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* 9. Footer */}
      <footer className="bg-neutral-900 text-white py-12 relative overflow-hidden">
        {/* Subtle gradient */}
        <div className="absolute inset-0 bg-gradient-to-t from-neutral-950 to-neutral-900"></div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <motion.div
              className="text-center md:text-left"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <img src={logoFull} alt="Aktivity" className="h-8 mb-2 mx-auto md:mx-0 brightness-0 invert" />
              <p className="text-neutral-400 text-sm">
                Because "Happy Sheets" Don't Tell the Whole Story.
              </p>
            </motion.div>
            <div className="flex items-center gap-6 text-sm">
              <motion.a
                href="#"
                className="text-neutral-400 hover:text-white transition-colors"
                whileHover={{ y: -2 }}
              >
                Privacy
              </motion.a>
              <motion.a
                href="#"
                className="text-neutral-400 hover:text-white transition-colors"
                whileHover={{ y: -2 }}
              >
                Terms
              </motion.a>
              <motion.a
                href="mailto:hello@aktivity.io"
                className="text-neutral-400 hover:text-white transition-colors"
                whileHover={{ y: -2 }}
              >
                hello@aktivity.io
              </motion.a>
            </div>
            <motion.button
              onClick={scrollToForm}
              className="px-6 py-2.5 border-2 border-orange-500 text-orange-500 hover:bg-orange-500 hover:text-white font-medium rounded-xl transition-all text-sm"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.98 }}
            >
              Join Waitlist
            </motion.button>
          </div>
          <motion.div
            className="mt-8 pt-8 border-t border-neutral-800 text-center text-neutral-500 text-sm"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            © {new Date().getFullYear()} Aktivity. All rights reserved.
          </motion.div>
        </div>
      </footer>
    </div>
  );
};

export default WaitlistPage;
